import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useT } from '../i18n/translations';
import { LANGUAGES } from '../i18n/translations';
import { CURRENCIES } from '../data/catalog';
import Icon from './Icon';
import './Header.css';

const NAV_ITEMS = [
  { key: 'home', page: 'home' },
  { key: 'games', page: 'category', params: { id: 'all' }, label: 'MyCard 點數' },
  { key: 'small', page: 'category', params: { id: 'small' }, label: '小額入門' },
  { key: 'medium', page: 'category', params: { id: 'medium' }, label: '熱銷面額' },
  { key: 'large', page: 'category', params: { id: 'large' }, label: '大額儲值' },
  { key: 'vip', page: 'vip' },
  { key: 'blog', page: 'blog' },
];

export default function Header() {
  const {
    lang,
    setLang,
    theme,
    toggleTheme,
    currency,
    setCurrency,
    cartCount,
    user,
    openDrawer,
    route,
    navigate,
  } = useApp();
  const t = useT(lang);
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClickAway = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  return (
    <header className={`nx-header ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="container nx-header__row">
        <button className="nx-logo" onClick={() => navigate('home')} aria-label="QQCARD">
          <span className="nx-logo__mark" />
          <span className="nx-logo__name">QQCARD</span>
        </button>

        <nav className="nx-nav" aria-label="Primary">
          {NAV_ITEMS.map((it) => {
            const active =
              route.page === it.page &&
              (!it.params || it.params.id === route.params?.id);
            return (
              <button
                key={it.key}
                className={`nx-nav__link ${active ? 'is-active' : ''}`}
                onClick={() => navigate(it.page, it.params || {})}
              >
                {it.label || t.nav[it.key]}
              </button>
            );
          })}
        </nav>

        <div className="nx-search-trigger" onClick={() => openDrawer('search')}>
          <Icon name="search" size={16} />
          <span>{t.nav.search}</span>
          <kbd>⌘K</kbd>
        </div>

        <div className="nx-actions" ref={menuRef}>
          <button
            className="nx-iconbtn"
            onClick={toggleTheme}
            aria-label="切換主題"
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={17} />
          </button>

          <div className="nx-dd">
            <button
              className="nx-iconbtn nx-dd__trig"
              onClick={() => setOpenMenu(openMenu === 'lang' ? null : 'lang')}
              aria-haspopup="true"
              aria-expanded={openMenu === 'lang'}
            >
              <Icon name="globe" size={16} />
              <span className="nx-dd__abbr">{lang.toUpperCase().split('-')[0]}</span>
            </button>
            {openMenu === 'lang' && (
              <div className="nx-dd__menu">
                <div className="nx-dd__title">語言</div>
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    className={`nx-dd__item ${lang === l.code ? 'is-active' : ''}`}
                    onClick={() => {
                      setLang(l.code);
                      setOpenMenu(null);
                    }}
                  >
                    <span className="nx-dd__flag">{l.flag}</span>
                    {l.label}
                    {lang === l.code && <Icon name="check" size={14} />}
                  </button>
                ))}
                <div className="nx-dd__divider" />
                <div className="nx-dd__title">貨幣</div>
                <div className="nx-dd__chips">
                  {Object.keys(CURRENCIES).map((c) => (
                    <button
                      key={c}
                      className={`nx-dd__chip ${currency === c ? 'is-active' : ''}`}
                      onClick={() => setCurrency(c)}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            className="nx-iconbtn nx-iconbtn--cart"
            onClick={() => openDrawer('cart')}
            aria-label={`${t.nav.cart} (${cartCount})`}
          >
            <Icon name="cart" size={18} />
            {cartCount > 0 && <span className="nx-badge-count">{cartCount}</span>}
          </button>

          {user ? (
            <button
              className="nx-avatar"
              onClick={() => openDrawer('account')}
              aria-label={user.name}
            >
              <span className="nx-avatar__face">{user.avatar}</span>
            </button>
          ) : (
            <button
              className="btn btn-primary btn-sm nx-cta"
              onClick={() => openDrawer('auth')}
            >
              {t.nav.signin}
            </button>
          )}

          <button
            className="nx-iconbtn nx-mobile-only"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="選單"
          >
            <Icon name={mobileOpen ? 'close' : 'menu'} size={17} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="nx-mobile-menu">
          <div className="container">
            {NAV_ITEMS.map((it) => (
              <button
                key={it.key}
                className="nx-mobile-menu__item"
                onClick={() => {
                  navigate(it.page, it.params || {});
                  setMobileOpen(false);
                }}
              >
                {it.label || t.nav[it.key]}
                <Icon name="arrowRight" size={14} />
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
